import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Question } from '../data/surveyQuestions';

interface SurveyResponse {
  questionId: string;
  value: number;
}

interface AppState {
  surveyResponses: Record<string, number>;
  currentUser: {
    name: string;
    role: string;
  };
  assessmentData: {
    empathy: number;
    growth: number;
    adaptability: number;
    purpose: number;
  };
}

type AppAction = 
  | { type: 'UPDATE_SURVEY_RESPONSE'; payload: { questionId: string; value: number } }
  | { type: 'RESET_SURVEY' }
  | { type: 'SET_ASSESSMENT_DATA'; payload: AppState['assessmentData'] };

const initialState: AppState = {
  surveyResponses: {},
  currentUser: {
    name: 'JD',
    role: 'Culture Analyst'
  },
  assessmentData: {
    empathy: 75,
    growth: 82,
    adaptability: 68,
    purpose: 90
  }
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'UPDATE_SURVEY_RESPONSE':
      return {
        ...state,
        surveyResponses: {
          ...state.surveyResponses,
          [action.payload.questionId]: action.payload.value
        }
      };
    case 'RESET_SURVEY':
      return {
        ...state,
        surveyResponses: {}
      };
    case 'SET_ASSESSMENT_DATA':
      return {
        ...state,
        assessmentData: action.payload
      };
    default:
      return state;
  }
};

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;